import { renderTemplateContent } from "../contentRenderer";
import { mergeTemplateDesign, templateDesignToCssVars } from "../design";
import { DefaultTemplateLink } from "../templateLink";
import type {
    ResolvedTemplateDesign,
    SiteTemplateDefinition,
    TemplateLinkComponent,
    TemplateRenderProps,
} from "../types";
import { WebBuilderPageRegion } from "../WebBuilderPageRegion";
import { WebGlobalBuilderRegion } from "../WebGlobalBuilderRegion";
import { webSystemTemplateDesignDefaults } from "../webSystemTemplateDesign";
import { IndustrialBlueprintStyles } from "./IndustrialBlueprintStyles";
import { TakeresRuntime } from "./TakeresRuntime";

function TakeresContrastStyles() {
    return (
        <style>{`
            [data-web-template="takeres"] [data-web-region="footer"] .site-builder-button,
            [data-web-template="takeres"] [data-web-region="footer"] .site-builder-button:is(:hover, :focus-visible),
            [data-web-template="takeres"] [data-web-region="footer"] .site-builder-button .site-builder-cta-label {
                color: #10211f !important;
            }
        `}</style>
    );
}

function TakeresFallbackPage({
    props,
    design,
    LinkComponent,
}: {
    props: TemplateRenderProps;
    design: ResolvedTemplateDesign;
    LinkComponent: TemplateLinkComponent;
}) {
    const page = props.page;

    if (!page) {
        return null;
    }

    return (
        <main className="px-5 py-20 sm:px-6" data-builder-document-region="page">
            <article className="mx-auto max-w-[900px] leading-8 text-[var(--site-muted)] [&_a]:text-[var(--site-primary)] [&_blockquote]:border-l-4 [&_blockquote]:border-[var(--site-accent)] [&_blockquote]:pl-5 [&_h1]:mb-7 [&_h1]:font-[var(--site-heading-font)] [&_h1]:text-6xl [&_h1]:font-extrabold [&_h1]:uppercase [&_h1]:leading-[.86] [&_h1]:tracking-[-.05em] [&_h1]:text-[var(--site-text)] [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:font-[var(--site-heading-font)] [&_h2]:text-4xl [&_h2]:font-extrabold [&_h2]:uppercase [&_h2]:text-[var(--site-text)] [&_li]:mb-2 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-5 [&_strong]:text-[var(--site-text)] [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6">
                <h1>{page.title}</h1>
                {renderTemplateContent(
                    page,
                    design,
                    LinkComponent,
                    props.leadForm,
                    props.readOnly,
                )}
            </article>
        </main>
    );
}

export const takeresTemplate: SiteTemplateDefinition = {
    manifest: {
        code: "takeres",
        name: "Takeres",
        type: "landing",
        category: "internal",
        description: "Private SaaS landing page template for the Takeres product website.",
        previewImage: "/templates/takeres/preview.jpg",
        visibility: "private",
        defaults: webSystemTemplateDesignDefaults.takeres,
        pageTypes: {
            home: { label: "Takeres homepage", slots: ["richContent"] },
            content: { label: "Takeres content page", slots: ["richContent"] },
        },
        editable: {
            colors: false,
            typography: false,
            radius: false,
            header: ["builder"],
            footer: ["builder"],
            buttons: ["solid"],
        },
    },
    render: (props: TemplateRenderProps) => {
        const design = mergeTemplateDesign(
            takeresTemplate.manifest.defaults,
            props.designOverride ?? props.site?.templateDesign,
        );
        const LinkComponent = props.LinkComponent ?? DefaultTemplateLink;

        return (
            <div
                className="web-industrial-blueprint min-h-screen overflow-x-hidden bg-[var(--site-background)] font-[var(--site-body-font)] text-[var(--site-text)] antialiased"
                data-industrial-template="takeres"
                data-web-template="takeres"
                data-web-template-editor={props.editorMode ? "true" : undefined}
                style={{
                    ...templateDesignToCssVars(design),
                    containerType: "inline-size",
                }}
            >
                <IndustrialBlueprintStyles />
                <TakeresContrastStyles />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="header"
                >
                    <TakeresRuntime
                        languageCode={props.page?.languageCode ?? props.site?.defaultLanguageCode}
                    />
                </WebGlobalBuilderRegion>
                <WebBuilderPageRegion
                    design={design}
                    fallback={(
                        <TakeresFallbackPage
                            design={design}
                            LinkComponent={LinkComponent}
                            props={props}
                        />
                    )}
                    LinkComponent={LinkComponent}
                    props={props}
                />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="footer"
                />
            </div>
        );
    },
};
